import React, { useState } from 'react';
import { Typography, Tag, Button, Space, Input, Card, Modal, Select, notification, Collapse, Badge, Spin, Drawer, Descriptions, Alert } from 'antd';
import { ReloadOutlined, PlayCircleOutlined, LinkOutlined, TableOutlined, SyncOutlined } from '@ant-design/icons';
import ProTable, { ProColumns } from '@ant-design/pro-table';
import { useQuery } from '@tanstack/react-query';
import axiosApiInstance from '../../api/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Workflow {
    workflow_id: string;
    run_id: string;
    status: string;
    channel_id: string;
    reporting_month: string;
    started_at: string;
    ended_at: string;
}

interface WorkflowsResponse {
    workflows: Workflow[];
    next_page_token: string;
}

interface FailedWorkflow {
    workflow_id: string;
    run_id: string;
    channel_id: string;
    started_at: string;
    ended_at: string;
}

interface FailureGroup {
    reason: string;
    count: number;
    workflows: FailedWorkflow[];
}

interface FailedWorkflowsResponse {
    total_failed: number;
    groups: FailureGroup[];
}

interface WorkflowDetail {
    workflow_id: string;
    run_id: string;
    status: string;
    channel_id: string;
    reporting_month: string;
    started_at: string;
    ended_at: string;
    task_queue: string;
    failure_reason: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Maps Temporal workflow status to an Ant Design Tag
const statusTag = (status: string) => {
    const map: Record<string, { color: string; label: string }> = {
        COMPLETED: { color: 'green', label: '✅ Done' },
        RUNNING:   { color: 'blue',  label: '🔄 Running' },
        FAILED:    { color: 'red',   label: '❌ Failed' },
        TERMINATED: { color: 'orange', label: '🛑 Terminated' },
    };
    const s = map[status.toUpperCase()] ?? { color: 'default', label: status };
    return <Tag color={s.color}>{s.label}</Tag>;
};

// Calculates human-readable duration between two ISO timestamps
const calcDuration = (start: string, end: string) => {
    if (!start || !end) return '-';
    const diff = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 1000);
    const m = Math.floor(diff / 60);
    const s = diff % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// ─── WorkflowDetailDrawer (Task 3.5) ─────────────────────────────────────────
// Slide-out drawer showing full details for a single workflow.
// Opens when clicking "View" in the main table.
// API: GET /api/youtubereports/workflows/:workflow_id

const WorkflowDetailDrawer: React.FC<{
    workflowId: string | null;
    onClose: () => void;
    onRestart: () => void;
}> = ({ workflowId, onClose, onRestart }) => {
    const { data, isLoading } = useQuery<WorkflowDetail>({
        queryKey: ['workflow-detail', workflowId],
        queryFn: () =>
            axiosApiInstance
                .get(`/api/youtubereports/workflows/${workflowId}`)
                .then(r => r.data),
        enabled: !!workflowId,
    });

    // Calls PUT /api/youtubereports/workflows/:id/restart
    const handleRestart = async () => {
        if (!workflowId) return;
        try {
            await axiosApiInstance.put(`/api/youtubereports/workflows/${workflowId}/restart`);
            notification.success({ message: 'Workflow restarted' });
            onRestart();
            onClose();
        } catch {
            notification.error({ message: 'Failed to restart workflow' });
        }
    };

    // Calls DELETE /api/youtubereports/workflows/:id
    const handlePurge = async () => {
        if (!workflowId) return;
        Modal.confirm({
            title: 'Purge workflow?',
            content: 'This will permanently delete the workflow history.',
            okType: 'danger',
            onOk: async () => {
                try {
                    await axiosApiInstance.delete(`/api/youtubereports/workflows/${workflowId}`);
                    notification.success({ message: 'Workflow purged' });
                    onClose();
                } catch {
                    notification.error({ message: 'Failed to purge workflow' });
                }
            },
        });
    };

    const temporalUrl = data
        ? `http://109.199.111.50:8233/namespaces/default/workflows/${data.workflow_id}/${data.run_id}`
        : '#';

    return (
        <Drawer
            open={!!workflowId}
            onClose={onClose}
            width={500}
            placement="right"
            title={data?.workflow_id ?? 'Workflow Details'}
        >
            {isLoading ? (
                <Spin />
            ) : data ? (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="Status">{statusTag(data.status)}</Descriptions.Item>
                        <Descriptions.Item label="Channel">{data.channel_id}</Descriptions.Item>
                        <Descriptions.Item label="Month">{dayjs(data.reporting_month).format('MMMM YYYY')}</Descriptions.Item>
                        <Descriptions.Item label="Started">{dayjs(data.started_at).format('MMM D, YYYY HH:mm:ss')}</Descriptions.Item>
                        <Descriptions.Item label="Ended">{dayjs(data.ended_at).format('MMM D, YYYY HH:mm:ss')}</Descriptions.Item>
                        <Descriptions.Item label="Duration">{calcDuration(data.started_at, data.ended_at)}</Descriptions.Item>
                        <Descriptions.Item label="Task Queue">{data.task_queue}</Descriptions.Item>
                    </Descriptions>

                    {data.failure_reason && (
                        <>
                            <Typography.Text strong>Failure Details</Typography.Text>
                            <Alert type="error" message={data.failure_reason} />
                        </>
                    )}

                    <Typography.Text strong>Actions</Typography.Text>
                    <Space>
                        {data.status.toUpperCase() === 'FAILED' && (
                            <Button icon={<ReloadOutlined />} onClick={handleRestart}>
                                Restart
                            </Button>
                        )}
                        <Button danger onClick={handlePurge}>
                            🗑 Purge
                        </Button>
                        <Button
                            type="link"
                            icon={<LinkOutlined />}
                            href={temporalUrl}
                            target="_blank"
                        >
                            Open in Temporal UI
                        </Button>
                    </Space>
                </Space>
            ) : null}
        </Drawer>
    );
};

// ─── FailedWorkflowsPanel (Task 3.4) ─────────────────────────────────────────
// Shows failed workflows grouped by failure reason using Levenshtein fuzzy matching.
// Supports per-group and per-workflow restart.
// API: GET /api/youtubereports/workflows/failed?month=YYYY-MM

const FailedWorkflowsPanel: React.FC<{ month: string; onRestart: () => void }> = ({ month, onRestart }) => {
    const [restartingIds, setRestartingIds] = useState<Set<string>>(new Set());
    const [confirmWorkflowId, setConfirmWorkflowId] = useState<string | null>(null);
    const [confirmRestartAll, setConfirmRestartAll] = useState<FailedWorkflow[] | null>(null);

    const { data, isLoading, refetch } = useQuery<FailedWorkflowsResponse>({
        queryKey: ['perf-report-failed', month],
        queryFn: () =>
            axiosApiInstance
                .get(`/api/youtubereports/workflows/failed?month=${month}`)
                .then(r => r.data),
    });

    // Restarts a single workflow — called after confirmation
    const doRestart = async (workflowId: string) => {
        setRestartingIds(prev => new Set(prev).add(workflowId));
        try {
            await axiosApiInstance.put(`/api/youtubereports/workflows/${workflowId}/restart`);
            notification.success({ message: `Restarted ${workflowId}` });
            refetch();
            onRestart();
        } catch {
            notification.error({ message: `Failed to restart ${workflowId}` });
        } finally {
            setRestartingIds(prev => { const s = new Set(prev); s.delete(workflowId); return s; });
        }
    };

    // Shows confirmation modal before restarting a single workflow
    const restartWorkflow = (workflowId: string) => {
    setConfirmWorkflowId(workflowId);
};

    // Shows confirmation modal before restarting all workflows in a group
    const restartAll = (workflows: FailedWorkflow[]) => {
    setConfirmRestartAll(workflows);
};

    if (isLoading) return <Spin />;
    if (!data?.groups?.length) return null;

    return (
        <>
        <Card
            title={`❌ Failed Workflows (${data.total_failed} total)`}
            style={{ marginBottom: 16 }}
            size="small"
        >
            <Collapse>
                {data.groups.map((group, idx) => (
                    <Collapse.Panel
                        key={String(idx)}
                        header={
                            <Space>
                                <Badge count={group.count} color="red" />
                                <Typography.Text strong style={{ maxWidth: 500 }}>
                                    {group.reason}
                                </Typography.Text>
                            </Space>
                        }
                        extra={
                            <Button
                                size="small"
                                danger
                                onClick={e => { e.stopPropagation(); restartAll(group.workflows); }}
                            >
                                Restart All
                            </Button>
                        }
                    >
                        {group.workflows.map(w => (
                            <div key={w.workflow_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                                <Space>
                                    <Typography.Text code>{w.channel_id || w.workflow_id}</Typography.Text>
                                    <Typography.Text type="secondary">
                                        {dayjs(w.started_at).format('MMM D, HH:mm')}
                                    </Typography.Text>
                                </Space>
                                <Button
                                    size="small"
                                    icon={<ReloadOutlined />}
                                    loading={restartingIds.has(w.workflow_id)}
                                    onClick={() => restartWorkflow(w.workflow_id)}
                                >
                                    Restart
                                </Button>
                            </div>
                        ))}
                    </Collapse.Panel>
                ))}
            </Collapse>
        </Card>
        <Modal
            open={!!confirmWorkflowId}
            title="Restart workflow?"
            onCancel={() => setConfirmWorkflowId(null)}
            footer={
                <Space>
                    <Button
                        type="primary"
                        onClick={() => {
                            if (confirmWorkflowId) doRestart(confirmWorkflowId);
                            setConfirmWorkflowId(null);
                        }}
                    >
                        OK
                    </Button>
                    <Button onClick={() => setConfirmWorkflowId(null)}>
                        Cancel
                    </Button>
                </Space>
            }
        >
            <p>Restart {confirmWorkflowId}?</p>
        </Modal>
        <Modal
    open={!!confirmRestartAll}
    title={`Restart ${confirmRestartAll?.length} failed workflows?`}
    onCancel={() => setConfirmRestartAll(null)}
    footer={
        <Space>
            <Button
                type="primary"
                onClick={async () => {
                    if (confirmRestartAll) {
                        await Promise.all(confirmRestartAll.map(w => doRestart(w.workflow_id)));
                    }
                    setConfirmRestartAll(null);
                }}
            >
                OK
            </Button>
            <Button onClick={() => setConfirmRestartAll(null)}>
                Cancel
            </Button>
        </Space>
    }
>
    <p>This will restart all {confirmRestartAll?.length} workflows in this group.</p>
</Modal>
        </>
    );
};

// ─── PodioCustomersSync ──────────────────────────────────────────────────────
// Monthly Podio → DB pipeline (replaces TestPodioUpdateCustomersAggregated).
// API: POST /api/youtubereports/podio-customers-sync
// API: GET  /api/youtubereports/podio-customers-sync/status

interface PodioSyncStatus {
    running: boolean;
}

const PodioCustomersSync: React.FC = () => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [starting, setStarting] = useState(false);

    const { data: statusData, refetch: refetchStatus } = useQuery<PodioSyncStatus>({
        queryKey: ['podio-customers-sync-status'],
        queryFn: () =>
            axiosApiInstance
                .get('/api/youtubereports/podio-customers-sync/status')
                .then(r => r.data),
        refetchInterval: (data) => (data?.running ? 5000 : false),
    });

    const running = statusData?.running ?? false;

    const handleStart = async () => {
        setStarting(true);
        try {
            await axiosApiInstance.post('/api/youtubereports/podio-customers-sync');
            notification.info({
                message: 'Podio sync started',
                description: "You'll get a notification when it finishes. This can take a long time.",
            });
            setConfirmOpen(false);
            await refetchStatus();
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 409) {
                notification.warning({ message: 'A Podio sync is already running' });
                await refetchStatus();
            } else {
                notification.error({ message: 'Failed to start Podio sync' });
            }
        } finally {
            setStarting(false);
        }
    };

    return (
        <>
            <Card title="Sync Podio customers & channels" style={{ marginBottom: 16 }} size="small">
                <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                    Updates customers, contracts, channels, and assignments from Podio (export, DB sync, assignment sheet patch).
                    Replaces the monthly Go test. Can take a long time; if the API restarts mid-run, the sync stops without a notification.
                </Typography.Paragraph>
                <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    loading={starting || running}
                    disabled={running && !starting}
                    onClick={() => setConfirmOpen(true)}
                >
                    {running ? 'Sync in progress…' : 'Run Podio sync'}
                </Button>
            </Card>
            <Modal
                open={confirmOpen}
                title="Run Podio customer sync?"
                onCancel={() => setConfirmOpen(false)}
                footer={
                    <Space>
                        <Button type="primary" loading={starting} onClick={handleStart}>
                            Start sync
                        </Button>
                        <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    </Space>
                }
            >
                <p>
                    This runs the full Podio export and database update in the background. You will receive an in-app notification when it completes successfully.
                </p>
            </Modal>
        </>
    );
};

// ─── StartNewRun (Task 3.3 + 3.6) ────────────────────────────────────────────
// Form to start a new batch of performance report workflows for a given month.
// Also contains the Update Tracking Sheet and Open Sheet buttons.
// API: POST /api/youtubereports/workflows/batch
// API: POST /api/youtubereports/workflows/spreadsheet-update

const StartNewRun: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
    const [selectedMonth, setSelectedMonth] = useState<string>(dayjs().subtract(1, 'month').format('YYYY-MM'));
    const [channels, setChannels] = useState<string[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sheetLoading, setSheetLoading] = useState(false);
    const [confirmSheet, setConfirmSheet] = useState(false);

    const handleStart = async () => {
        setLoading(true);
        try {
            const res = await axiosApiInstance.post('/api/youtubereports/workflows/batch', {
                month: parseInt(selectedMonth.split('-')[1]),
                year: parseInt(selectedMonth.split('-')[0]),
                channel_ids: channels.length > 0 ? channels : undefined,
            });
            const { started, skipped } = res.data;
            notification.success({
                message: 'Run Started',
                description: `Started ${started} workflows, skipped ${skipped}`,
            });
            setModalOpen(false);
            onSuccess();
        } catch (e) {
            notification.error({ message: 'Failed to start run' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Card title="Start New Run" style={{ marginBottom: 16 }} size="small">
                <Space wrap>
                    <DatePicker
                        picker="month"
                        value={dayjs(selectedMonth)}
                        onChange={(d: Dayjs | null) => d && setSelectedMonth(d.format('YYYY-MM'))}
                    />
                    <Select
                        mode="tags"
                        placeholder="Channel IDs (optional — leave empty for all)"
                        value={channels}
                        onChange={setChannels}
                        style={{ minWidth: 340 }}
                        tokenSeparators={[',']}
                    />
                    <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={() => setModalOpen(true)}
                    >
                        Start Run
                    </Button>

                    {/* Updates the Google Sheets tracking spreadsheet for the selected month */}
                    <Button
                        icon={<TableOutlined />}
                        loading={sheetLoading}
                        onClick={() => setConfirmSheet(true)}
                    >
                        📊 Update Tracking Sheet
                    </Button>

                    {/* Opens the tracking spreadsheet in a new tab */}
                    <Button
                        icon={<LinkOutlined />}
                        href="https://docs.google.com/spreadsheets/d/1BdhQDURgoJn1x7DEUX1nfQedH4PAuMWd868YqQ63BhI"//test: https://docs.google.com/spreadsheets/d/1VeN0I6u8fOIicBdjQIpZCPfoUG8oCZI5zCBgqswUu_g
                        target="_blank"
                        type="link"
                    >
                        🔗 Open Sheet
                    </Button>
                </Space>
            </Card>

            {/* Confirm Start Run */}
            <Modal
                open={modalOpen}
                title="Confirm Start Run"
                onCancel={() => setModalOpen(false)}
                footer={
                    <Space>
                        <Button type="primary" loading={loading} onClick={handleStart}>
                            Start
                        </Button>
                        <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                    </Space>
                }
            >
                <p>
                    Start <strong>{dayjs(selectedMonth).format('MMMM YYYY')}</strong> performance reports
                    {channels.length > 0 ? ` for ${channels.length} channel(s)?` : ' for all channels?'}
                </p>
            </Modal>

            {/* Confirm Update Tracking Sheet */}
            <Modal
                open={confirmSheet}
                title={`Update tracking spreadsheet for ${dayjs(selectedMonth).format('MMMM YYYY')}?`}
                onCancel={() => setConfirmSheet(false)}
                footer={
                    <Space>
                        <Button
                            type="primary"
                            loading={sheetLoading}
                            onClick={async () => {
                                setSheetLoading(true);
                                try {
                                    const res = await axiosApiInstance.post('/api/youtubereports/workflows/spreadsheet-update', {
                                        month: parseInt(selectedMonth.split('-')[1]),
                                        year: parseInt(selectedMonth.split('-')[0]),
                                    });
                                    const { updated_channels, statuses } = res.data;
                                    notification.success({
                                        message: 'Spreadsheet Updated',
                                        description: `Updated ${updated_channels} channels: ${statuses.YES} YES, ${statuses.NO} NO, ${statuses['NO*']} NO*`,
                                    });
                                    setConfirmSheet(false);
                                } catch {
                                    notification.error({ message: 'Failed to update spreadsheet' });
                                } finally {
                                    setSheetLoading(false);
                                }
                            }}
                        >
                            OK
                        </Button>
                        <Button onClick={() => setConfirmSheet(false)}>Cancel</Button>
                    </Space>
                }
            />
        </>
    );
};

// ─── PerformanceReports (Task 3.1 + 3.2) ─────────────────────────────────────
// Main page component at /tools/performance_reports.
// Shows a live table of workflow runs for a selected month with filtering.
// Polls every 5 seconds when any workflow is in RUNNING state.
// API: GET /api/youtubereports/workflows?month=YYYY-MM&status=...

const PerformanceReports: React.FC = () => {
    const [month, setMonth] = useState<string>(dayjs().subtract(1, 'month').format('YYYY-MM'));
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [channelSearch, setChannelSearch] = useState<string>('');
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [confirmBulkRestart, setConfirmBulkRestart] = useState(false);
    const [confirmBulkPurge, setConfirmBulkPurge] = useState(false);

    // Quick-selects only failed workflows
    const selectAllFailed = () => {
        const failedKeys = (data?.workflows ?? [])
            .filter(w => w.status.toUpperCase() === 'FAILED')
            .map(w => w.run_id);
        setSelectedRowKeys(failedKeys);
    };

    // Opens bulk restart confirmation modal
    const restartSelected = () => setConfirmBulkRestart(true);

    // Opens bulk purge confirmation modal
    const purgeSelected = () => setConfirmBulkPurge(true);

    const { data, isLoading, refetch } = useQuery<WorkflowsResponse>({
        queryKey: ['perf-report-workflows', month, statusFilter],
        queryFn: () =>
            axiosApiInstance
                .get(`/api/youtubereports/workflows?month=${month}${statusFilter ? `&status=${statusFilter}` : ''}`)
                .then(r => r.data),
        staleTime: 0,
        // Auto-refresh every 5s while any workflow is running
        refetchInterval: (data) => {
            const hasRunning = data?.workflows?.some(w => w.status === 'RUNNING');
            return hasRunning ? 5000 : false;
        },
    });

    // Client-side channel filter applied on top of the API response
    const filtered = (data?.workflows ?? []).filter(w =>
        channelSearch ? w.channel_id.toLowerCase().includes(channelSearch.toLowerCase()) : true
    );

    const columns: ProColumns<Workflow>[] = [
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_, record) => statusTag(record.status),
        },
        {
            title: 'Channel',
            dataIndex: 'channel_id',
            key: 'channel_id',
        },
        {
            title: 'Month',
            dataIndex: 'reporting_month',
            key: 'reporting_month',
        },
        {
            title: 'Started',
            dataIndex: 'started_at',
            key: 'started_at',
            render: (_, r) => dayjs(r.started_at).format('MMM D, HH:mm'),
        },
        {
            title: 'Duration',
            key: 'duration',
            render: (_, r) => calcDuration(r.started_at, r.ended_at),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {/* Opens the workflow detail drawer */}
                    <Button size="small" type="link" onClick={() => setSelectedWorkflowId(record.workflow_id)}>View</Button>
                    {/* Restart button only shown for failed workflows */}
                    {record.status.toUpperCase() === 'FAILED' && (
                        <Button size="small" icon={<ReloadOutlined />} type="link" />
                    )}
                </Space>
            ),
        },
    ];

    const { data: countersData } = useQuery<WorkflowsResponse>({
    queryKey: ['perf-report-counters', month],
    queryFn: () =>
        axiosApiInstance
            .get(`/api/youtubereports/workflows?month=${month}`)
            .then(r => r.data),
    staleTime: 0,
    refetchInterval: 5000,
});

    const runningCount = (countersData?.workflows ?? []).filter(w => w.status.toUpperCase() === 'RUNNING').length;
const completedCount = (countersData?.workflows ?? []).filter(w => w.status.toUpperCase() === 'COMPLETED').length;
const failedCount = (countersData?.workflows ?? []).filter(w => w.status.toUpperCase() === 'FAILED').length;

    return (
        <div style={{ padding: 24 }}>
            <Typography.Title level={2}>Performance Reports</Typography.Title>
            <Space style={{ marginBottom: 16 }}>
                <Tag color="blue">🔄 Running: {runningCount}</Tag>
                <Tag color="green">✅ Completed: {completedCount}</Tag>
                <Tag color="red">❌ Failed: {failedCount}</Tag>
            </Space>
            <FailedWorkflowsPanel month={month} onRestart={() => refetch()} />
            <PodioCustomersSync />
            <StartNewRun onSuccess={() => refetch()} />
            <Space style={{ marginBottom: 16 }}>
                <DatePicker
                    picker="month"
                    value={dayjs(month)}
                    onChange={(d: Dayjs | null) => d && setMonth(d.format('YYYY-MM'))}
                />
                <Input.Search
                    placeholder="Search channel..."
                    allowClear
                    onSearch={setChannelSearch}
                    onChange={e => !e.target.value && setChannelSearch('')}
                    style={{ width: 220 }}
                />
                <Select
    placeholder="Filter by status"
    allowClear
    style={{ width: 160 }}
    onChange={(val) => setStatusFilter(val ?? '')}
    options={[
        { value: 'running', label: '🔄 Running' },
        { value: 'completed', label: '✅ Completed' },
        { value: 'failed', label: '❌ Failed' },
        { value: 'canceled', label: 'Canceled' },
        { value: 'timed_out', label: 'Timed Out' },
        { value: 'terminated', label: 'Terminated' },
    ]}
/>
            </Space>
            <ProTable<Workflow>
                columns={columns}
                dataSource={filtered}
                rowKey="run_id"
                loading={isLoading || bulkLoading}
                search={false}
                pagination={{ pageSize: 20 }}
                options={{
                    reload: async () => { await refetch(); },
                    setting: true,
                    density: true,
                }}
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys as string[]),
                    getCheckboxProps: (record) => ({
                        disabled: record.status.toUpperCase() === 'RUNNING',
                    }),
                }}
                tableAlertRender={({ selectedRowKeys }) => (
                    <span>Selected {selectedRowKeys.length} {selectedRowKeys.length === 1 ? 'item' : 'items'}</span>
                )}
                tableAlertOptionRender={() => (
                    <Space>
                        <Button size="small" onClick={selectAllFailed}>Select All Failed</Button>
                        <Button size="small" type="primary" icon={<ReloadOutlined />} onClick={restartSelected} disabled={selectedRowKeys.length === 0}>
                            Restart Selected ({selectedRowKeys.length})
                        </Button>
                        <Button size="small" danger onClick={purgeSelected} disabled={selectedRowKeys.length === 0}>
                            🗑 Purge Selected
                        </Button>
                    </Space>
                )}
            />

            <WorkflowDetailDrawer
                workflowId={selectedWorkflowId}
                onClose={() => setSelectedWorkflowId(null)}
                onRestart={() => refetch()}
            />

            {/* Bulk restart confirmation */}
            <Modal
                open={confirmBulkRestart}
                title={`Restart ${selectedRowKeys.length} workflows?`}
                onCancel={() => setConfirmBulkRestart(false)}
                footer={
                    <Space>
                        <Button type="primary" onClick={async () => {
                            setConfirmBulkRestart(false);
                            setBulkLoading(true);
                            let count = 0;
                            for (const runId of selectedRowKeys) {
                                const workflow = data?.workflows?.find(w => w.run_id === runId);
                                if (!workflow) continue;
                                try {
                                    await axiosApiInstance.put(`/api/youtubereports/workflows/${workflow.workflow_id}/restart`);
                                    count++;
                                    notification.info({ message: `Restarted ${count}/${selectedRowKeys.length}...`, duration: 1 });
                                } catch {
                                    notification.error({ message: `Failed to restart ${workflow.workflow_id}` });
                                }
                            }
                            notification.success({ message: `Restarted ${count} workflows` });
                            setSelectedRowKeys([]);
                            setBulkLoading(false);
                            refetch();
                        }}>OK</Button>
                        <Button onClick={() => setConfirmBulkRestart(false)}>Cancel</Button>
                    </Space>
                }
            >
                <p>This will restart {selectedRowKeys.length} selected workflows.</p>
            </Modal>

            {/* Bulk purge confirmation */}
            <Modal
                open={confirmBulkPurge}
                title={`Purge ${selectedRowKeys.length} workflows?`}
                onCancel={() => setConfirmBulkPurge(false)}
                footer={
                    <Space>
                        <Button type="primary" danger onClick={async () => {
                            setConfirmBulkPurge(false);
                            setBulkLoading(true);
                            let count = 0;
                            for (const runId of selectedRowKeys) {
                                const workflow = data?.workflows?.find(w => w.run_id === runId);
                                if (!workflow) continue;
                                try {
                                    await axiosApiInstance.delete(`/api/youtubereports/workflows/${workflow.workflow_id}`);
                                    count++;
                                } catch {
                                    notification.error({ message: `Failed to purge ${workflow.workflow_id}` });
                                }
                            }
                            notification.success({ message: `Purged ${count} workflows` });
                            setSelectedRowKeys([]);
                            setBulkLoading(false);
                            refetch();
                        }}>OK</Button>
                        <Button onClick={() => setConfirmBulkPurge(false)}>Cancel</Button>
                    </Space>
                }
            >
                <p>This will permanently delete the history of {selectedRowKeys.length} workflows.</p>
            </Modal>
        </div>
    );
};

export default PerformanceReports;